import { Attribute, Directive, ElementRef, OnDestroy, OnInit, Optional } from '@angular/core';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Subscription } from 'rxjs';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GtmService } from '@df/module-gtm/gtm.service';

@Directive()
export abstract class AbstractGtmTrack<T extends Gtm.ITrackData> implements OnInit, OnDestroy {
    protected _sent = false;

    protected visibilitySubscription?: Subscription;

    constructor(
        protected elementRef: ElementRef,
        protected visibilityService: VisibilityService,
        protected gtmService: GtmService,
        @Optional() @Attribute('threshold') public threshold: string,
        @Optional() @Attribute('promoMode') public promoMode: Gtm.ITrackMode
    ) {}

    ngOnInit() {
        const mode = this.getMode();

        if (mode === Gtm.ITrackMode.OnView) {
            const threshold = this.getThreshold();

            this.visibilitySubscription = this.visibilityService
                .isInView(this.elementRef, { rootMargin: threshold ? `${threshold}px` : undefined })
                .subscribe(visible => {
                    if (visible && !this.sent) {
                        this.sendEvent();
                    }
                });
        } else {
            this.sendEvent();
        }
    }

    ngOnDestroy() {
        this.unsubscribe();
    }

    get sent(): boolean {
        return this._sent;
    }

    set sent(value: boolean) {
        this._sent = value;
        this.unsubscribe();
    }

    getThreshold(): number | undefined {
        if (this.threshold) {
            return parseInt(this.threshold, 10);
        } else {
            const data = this.getData();
            return data?.threshold;
        }
    }

    getMode(): Gtm.ITrackMode {
        if (this.promoMode) {
            return this.promoMode;
        } else {
            return this.getThreshold() ? Gtm.ITrackMode.OnView : Gtm.ITrackMode.OnLoad;
        }
    }

    unsubscribe() {
        if (this.visibilitySubscription) {
            this.visibilitySubscription.unsubscribe();
        }
    }

    protected abstract getData(): T | undefined;

    protected abstract sendEvent(): void;
}
